import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import {
  BookingStatus,
  RelocationInclusionType,
  UpdateRelocationCompletedTaskInput,
} from "@/gql/graphql";
import { Task } from "@/gql/schema";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { BookingRecord } from "@/app/Bookings/GraphQL/bookingRecordQuery";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowPathIcon, CheckIcon } from "@heroicons/react/20/solid";
import { Link } from "@/components/catalyst/link";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { EmptyState } from "@/lib/Components/EmptyState/EmptyState";
import { IllustrationsDocuments } from "@/assets/Illustrations";
import { RelocationUnfulfilledForm } from "@/app/Tasks/RelocationUnfulfilledForm";
import { BookingCard } from "@/app/Bookings/Components/BookingCard";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { FC } from "react";
import { Badge, BadgeColor } from "@/components/catalyst/badge";
import { CircleDollarSignIcon, FuelIcon, ShipIcon } from "lucide-react";
import { Strong, Text } from "@/components/catalyst/text";
import { updateRelocationCompletedTask } from "@/app/Tasks/GraphQL/taskMutations";
import { toFormikValidate } from "@/lib/Zod/zod-formik-adapter";
import { relocationFulfillmentValidation } from "@/app/Tasks/Validation/relocationFulfillmentValidation";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { useBooking } from "@/app/Bookings/Hooks/useBooking";
import { useTranslations } from "use-intl";

export function BookingCompleteTaskDialog({
  task,
  tab,
}: {
  task: Pick<Task, "id" | "related_to_id">;
  tab?: "completed" | "not_completed";
}) {
  const { close } = useDialogState();
  const booking = useBooking(task.related_to_id);
  const { mutateAsync } = useGqlMutation(updateRelocationCompletedTask);
  const t = useTranslations("task.completeDialog");

  return (
    <BaseDialog title={t("title")} className="space-y-6">
      <div className="rounded-md bg-gray-50 p-4">
        <BookingCard bookingId={booking.id} />
        <FuelDetails booking={booking} />
        <FerryDetails booking={booking} />
      </div>
      <Tabs defaultValue={tab ?? "undefined"} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="completed">
            <CheckIcon className="mr-2 h-4 w-4 text-green-500" />
            {t("tabCompleted")}
          </TabsTrigger>
          <TabsTrigger value="not_completed">
            <XMarkIcon className="mr-2 h-4 w-4 text-red-500" />
            {t("tabNotCompleted")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="undefined">
          <EmptyState
            header={t("emptyStateHeader")}
            subheader={t("emptyStateSubheader")}
            Icon={IllustrationsDocuments}
          />
        </TabsContent>
        <TabsContent value="completed">
          <FulfilledTransactionTable booking={booking} completed={true} />
          <Text className="mx-auto max-w-2xl p-6 text-center">
            {t.rich("completedDescription", {
              strong: (chunks) => <Strong>{chunks}</Strong>,
            })}
          </Text>
          <GenericForm<UpdateRelocationCompletedTaskInput>
            initialValues={{
              was_relocation_completed: true,
            }}
            onSubmit={async (values) => {
              await mutateAsync({
                id: task.id,
                input: values,
              });
              await close();
            }}
          >
            <DialogSubmitButton text={t("completeBooking")} Icon={CheckIcon} />
          </GenericForm>
        </TabsContent>
        <TabsContent value="not_completed">
          <FulfilledTransactionTable booking={booking} completed={false} />
          <Text className="mx-auto max-w-2xl p-6 text-center">
            {t.rich("notCompletedDescription", {
              strong: (chunks) => <Strong>{chunks}</Strong>,
            })}
          </Text>
          <GenericForm<UpdateRelocationCompletedTaskInput>
            validate={toFormikValidate(relocationFulfillmentValidation)}
            initialValues={{
              was_relocation_completed: false,
              cancellation_status: BookingStatus.CustomerCancelledNoShow,
              description: "",
            }}
            onSubmit={async (values) => {
              await mutateAsync({
                id: task.id,
                input: values,
              });
              await close();
            }}
          >
            <RelocationUnfulfilledForm />
            <DialogSubmitButton text={t("cancelBooking")} Icon={XMarkIcon} />
          </GenericForm>
        </TabsContent>
      </Tabs>
    </BaseDialog>
  );
}

function FuelDetails({ booking }: { booking: BookingRecord }) {
  const t = useTranslations("task.completeDialog");
  const supplier = booking.supplier;

  const fuelInclusion = booking.inclusions.find(
    (inclusion) => inclusion.type === RelocationInclusionType.Fuel,
  );

  if (
    !supplier.imoova_refunds_fuel ||
    !fuelInclusion?.value ||
    fuelInclusion.value <= 0
  ) {
    return null;
  }

  return (
    <div className="mt-3 flex items-center gap-x-3 border-t border-gray-200 pt-3">
      <FuelIcon className="h-4 w-4 text-gray-400" />
      <div className="text-xs text-gray-600">
        <span className="font-medium text-gray-900">{t("fuelRefund")}:</span>{" "}
        {t("upTo", {
          amount: formatCurrency(fuelInclusion.value, booking.currency),
        })}
        {supplier.fuel_refund_fee > 0 ? (
          <>
            {" "}
            · {t("fuelRefundAdminFee")}:{" "}
            {formatCurrency(supplier.fuel_refund_fee, booking.currency)}
          </>
        ) : null}
      </div>
    </div>
  );
}

function FerryDetails({ booking }: { booking: BookingRecord }) {
  const t = useTranslations("task.completeDialog");
  const supplier = booking.supplier;

  const ferryInclusion = booking.inclusions.find(
    (inclusion) => inclusion.type === RelocationInclusionType.Ferry,
  );

  if (
    !supplier.imoova_refunds_ferry ||
    !ferryInclusion?.value ||
    ferryInclusion.value <= 0
  ) {
    return null;
  }

  return (
    <div className="mt-3 flex items-center gap-x-3 border-t border-gray-200 pt-3">
      <ShipIcon className="h-4 w-4 text-gray-400" />
      <div className="text-xs text-gray-600">
        <span className="font-medium text-gray-900">{t("ferryRefund")}:</span>{" "}
        {t("upTo", {
          amount: formatCurrency(ferryInclusion.value, booking.currency),
        })}
        {supplier.ferry_refund_fee > 0 ? (
          <>
            {" "}
            · {t("ferryRefundAdminFee")}:{" "}
            {formatCurrency(supplier.ferry_refund_fee, booking.currency)}
          </>
        ) : null}
      </div>
    </div>
  );
}

function FulfilledTransactionTable({
  completed,
  booking,
}: {
  completed: boolean;
  booking: BookingRecord;
}) {
  const t = useTranslations("task.completeDialog");
  const firstPayment = booking.customerPayments[0];

  const relocation = booking.relocation;
  const supplier = booking.supplier;
  const contact = supplier.billingEntity!;

  const platformDeposit = booking.charge_summary.platform_refund_amount;

  const fulfilled: Transaction[] = [];

  if (completed) {
    fulfilled.push({
      amount: formatCurrency(
        relocation.charge_per_move_amount,
        booking.currency,
      ),
      status: t("addedToInvoice"),
      color: "zinc",
      client: contact?.name ?? "--",
      description: t("relocationCharge"),
      icon: CircleDollarSignIcon,
    });
  }

  if (platformDeposit) {
    fulfilled.push({
      paymentId: firstPayment?.id,
      amount: formatCurrency(platformDeposit, booking.currency),
      status: completed ? t("refundToCustomer") : t("withheldByImoova"),
      color: completed ? "green" : "red",
      client: booking.name,
      description: t("depositRefund"),
      icon: ArrowPathIcon,
    });
  }

  return <Table transactions={fulfilled} />;
}

type Transaction = {
  amount: string;
  subtext?: string;
  status: string;
  color: BadgeColor;
  client: string;
  description: string;
  icon: FC<any>;
  paymentId?: string;
};

function Table({ transactions }: { transactions: Transaction[] }) {
  const t = useTranslations("task.completeDialog");

  return (
    <div>
      <div className="mt-6 overflow-hidden border-t border-gray-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-none">
            <table className="w-full text-left">
              <thead className="sr-only">
                <tr>
                  <th>{t("thAmount")}</th>
                  <th className="hidden sm:table-cell">{t("thClient")}</th>
                  <th>{t("thMoreDetails")}</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((transaction, idx) => (
                  <tr key={idx}>
                    <td className="relative py-5 pr-6">
                      <div className="flex gap-x-6">
                        <transaction.icon
                          className="hidden h-6 w-5 flex-none text-gray-400 sm:block"
                          aria-hidden="true"
                        />
                        <div className="flex-auto">
                          <div className="flex items-start gap-x-3">
                            <div>
                              <div className="text-sm font-medium leading-6 text-gray-900">
                                {transaction.amount}
                              </div>
                              {transaction.subtext ? (
                                <div className="text-xs text-gray-500">
                                  {transaction.subtext}
                                </div>
                              ) : null}
                            </div>
                            <Badge color={transaction.color}>
                              {transaction.status}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="absolute bottom-0 right-full h-px w-screen bg-gray-100" />
                      <div className="absolute bottom-0 left-0 h-px w-screen bg-gray-100" />
                    </td>
                    <td className="hidden py-5 pr-6 sm:table-cell">
                      <div className="text-sm leading-6 text-gray-900">
                        {transaction.client}
                      </div>
                      <div className="mt-1 text-xs leading-5 text-gray-500">
                        {transaction.description}
                      </div>
                    </td>
                    <td className="py-5 text-right">
                      {transaction.paymentId ? (
                        <>
                          <div className="flex justify-end">
                            <Link
                              from="/$tenant/$orgId/supplier/{-$supplierId}"
                              to={
                                "/$tenant/$orgId/supplier/{-$supplierId}/payments/$id"
                              }
                              params={(prev) => ({
                                ...prev,
                                id: transaction.paymentId!,
                              })}
                              search={{}}
                              className="text-sm font-medium leading-6 text-indigo-600 hover:text-indigo-500"
                            >
                              {t("view")}
                              <span className="hidden sm:inline">
                                {" "}
                                {t("transaction")}
                              </span>
                              <span className="sr-only">
                                , {t("payment")} #{transaction.paymentId},{" "}
                                {transaction.client}
                              </span>
                            </Link>
                          </div>
                          <div className="mt-1 text-xs leading-5 text-gray-500">
                            {t("payment")}{" "}
                            <span className="text-gray-900">
                              #{transaction.paymentId}
                            </span>
                          </div>
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
